import { ErrorHandler } from '@src/shared/ui/components/ErrorHandler';
import { Table as TableComponent } from '@src/shared/ui/components/TableStatic';
import { Link } from '@uhg-abyss/web/ui/Link';
import { ToggleSwitch } from '@uhg-abyss/web/ui/ToggleSwitch';
import { isArray } from 'lodash';
import React, { useMemo } from 'react';

import configuration from './includes/configuration.json';

/**
 * Table
 *
 * Displays related entities from IRE in a table format.
 *
 * @param {Object} props - Component props.
 * @param {Array<Object>} props.rows - Array of row data for the table.
 * @returns {JSX.Element} Rendered table component.
 *
 * Cell Render Functions:
 * - renderCellEntityLink: Renders a link to the entity resume page.
 * - renderCellMatchKeyPositive: Renders positive match keys in green.
 * - renderCellMatchKeyNegative: Renders negative match keys in red.
 * - renderCellDataSources: Renders a comma-separated list of data sources.
 * - renderCellRisk: Renders risk value or masked if not selected.
 * - renderCellDob: Renders DOB value or masked if not selected.
 * - renderCellActions: Renders a toggle switch for row selection.
 */
export function Table({ rows = [] }) {
  /**
   * Renders the cell for the Entity Link column.
   *
   * @param {Object} params - Parameters object.
   * @param {Object} params.cell - Cell data.
   * @returns {JSX.Element|null} Link to entity resume or null.
   */
  const renderCellEntityLink = ({ cell }) => {
    const entityId = cell.value;
    if (!entityId) return null;
    return <Link href={`https://ire-portal.optum.com/entityResume?entityId=${entityId}`}>{entityId}</Link>;
  };

  /**
   * Renders the cell for the Match Key + column.
   *
   * @param {Object} params - Parameters object.
   * @param {Object} params.cell - Cell data.
   * @returns {JSX.Element|null} Positive match keys styled in green or null.
   */
  const renderCellMatchKeyPositive = ({ cell }) => {
    return isArray(cell.value) ? (
      <span style={{ color: 'var(--abyss-colors-success1)' }}>{cell.value.join(', ')}</span>
    ) : null;
  };

  /**
   * Renders the cell for the Match Key - column.
   *
   * @param {Object} params - Parameters object.
   * @param {Object} params.cell - Cell data.
   * @returns {JSX.Element|null} Negative match keys styled in red or null.
   */
  const renderCellMatchKeyNegative = ({ cell }) => {
    return isArray(cell.value) ? (
      <span style={{ color: 'var(--abyss-colors-error1)' }}>{cell.value.join(', ')}</span>
    ) : null;
  };

  /**
   * Renders the cell for the Data Source(s) column.
   *
   * @param {Object} params - Parameters object.
   * @param {Object} params.cell - Cell data.
   * @returns {string|null} Comma-separated data sources or value.
   */
  const renderCellDataSources = ({ cell }) => {
    if (isArray(cell.value)) {
      return cell.value.join(', ');
    }
    return cell.value;
  };

  /**
   * Renders the cell for the Risk column.
   *
   * @param {Object} params - Parameters object.
   * @param {Object} params.cell - Cell data.
   * @param {Object} params.row - Row data.
   * @returns {string} Risk value or masked.
   */
  const renderCellRisk = ({ cell, row }) => {
    const { value } = cell;
    const { isSelected } = row;
    if (!isSelected) {
      return '*';
    }
    return value;
  };

  /**
   * Renders the cell for the DOB column.
   *
   * @param {Object} params - Parameters object.
   * @param {Object} params.cell - Cell data.
   * @param {Object} params.row - Row data.
   * @returns {string} DOB value or masked.
   */
  const renderCellDob = ({ cell, row }) => {
    const { value } = cell;
    const { isSelected } = row;
    if (!isSelected) {
      return '*'.repeat(10);
    }
    return value;
  };

  /**
   * Renders the cell for the Show More (Actions) column.
   *
   * @param {Object} args - Arguments object.
   * @param {Object} args.row - Row data.
   * @param {Function} args.toggleRowSelected - Function to toggle row selection.
   * @returns {JSX.Element|null} Toggle switch for row selection.
   */
  const renderCellActions = (args) => {
    const { row, toggleRowSelected } = args;
    const { id, isSelected } = row;

    return (
      <ToggleSwitch
        hideLabel
        isChecked={isSelected}
        onChange={() => {
          toggleRowSelected(id);
        }}
      />
    );
  };

  /**
   * Memoized columns configuration for the table.
   *
   * @returns {Array<Object>} Array of column definitions.
   */
  const columns = useMemo(() => {
    return configuration?.initialColumns
      .map((item) => {
        const column = { ...item };

        if (column.Header === 'Entity Link') {
          column.Cell = renderCellEntityLink;
        }
        if (column.Header === 'Match Key +') {
          column.Cell = renderCellMatchKeyPositive;
        }
        if (column.Header === 'Match Key -') {
          column.Cell = renderCellMatchKeyNegative;
        }
        if (column.Header === 'Data Source(s)') {
          column.Cell = renderCellDataSources;
        }
        if (column.Header === 'Risk') {
          column.Cell = renderCellRisk;
        }
        if (column.Header === 'DOB') {
          column.Cell = renderCellDob;
        }
        if (column.Header === 'Show More') {
          column.Cell = renderCellActions;
        }

        return column;
      })
      .filter((column) => {
        return !['actions', 'dob', 'gender', 'risk'].includes(column.accessor);
      });
  }, []);

  return (
    <ErrorHandler location="src/widgets/RiskRecords/IreRelatedEntities/components/Table/Table.jsx">
      <TableComponent
        {...{
          accessor: 'eidSearch-IreRelatedEntities',
          columns,
          configuration,
          rows,
        }}
      />
    </ErrorHandler>
  );
}
